import { useCallback, useState } from "react";
import type { Editor } from "@tiptap/react";

const COLORS = [
  "#000000",
  "#374151",
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
  "#2563eb",
  "#7c3aed",
  "#db2777",
];

const HIGHLIGHT_COLORS = [
  "transparent",
  "#fef08a",
  "#bbf7d0",
  "#bfdbfe",
  "#fbcfe8",
];

const FONT_SIZES = ["12", "14", "16", "18", "24", "32"];

export { COLORS, HIGHLIGHT_COLORS, FONT_SIZES };

export function useEditorToolbar(editor: Editor | null) {
  const [colorOpen, setColorOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);

  const toggleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run();
  }, [editor]);

  const toggleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run();
  }, [editor]);

  const setColor = useCallback(
    (color: string) => {
      editor?.chain().focus().setColor(color).run();
      setColorOpen(false);
    },
    [editor],
  );

  const setHighlight = useCallback(
    (color: string) => {
      if (color === "transparent") {
        editor?.chain().focus().unsetHighlight().run();
      } else {
        editor?.chain().focus().setHighlight({ color }).run();
      }
      setHighlightOpen(false);
    },
    [editor],
  );

  const setFontSize = useCallback(
    (size: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor?.chain().focus() as any)?.setFontSize(size + "px").run();
    },
    [editor],
  );

  const setAlign = useCallback(
    (alignment: "right" | "center" | "left" | "justify") => {
      editor?.chain().focus().setTextAlign(alignment).run();
    },
    [editor],
  );

  const isActive = useCallback(
    (name: string, attrs?: Record<string, unknown>) =>
      editor?.isActive(name, attrs) ?? false,
    [editor],
  );

  return {
    colorOpen,
    setColorOpen,
    highlightOpen,
    setHighlightOpen,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    setColor,
    setHighlight,
    setFontSize,
    setAlign,
    isActive,
  };
}
